import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, Building2, Check, Loader2, CheckCircle2 } from "lucide-react";
import Seo from "@/components/site/Seo";
import Marquee from "@/components/site/Marquee";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { CONTACT } from "@/lib/site";
import { gaEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCT_TYPES = ["Doors", "Windows", "Doors and Windows"];

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  postcode: "",
  product_type: "",
  project_details: "",
  preferred_contact_time: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#quote") {
      const el = document.getElementById("quote");
      if (el) {
        setTimeout(() => {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
          else el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.postcode) {
      toast.error("Please fill in your name, phone, email and postcode.");
      return;
    }
    if (!form.product_type) {
      toast.error("Please choose a product type.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      // GA4 lead event — no PII, only non-identifying product type
      gaEvent("generate_lead", { product_type: form.product_type });
      setDone(true);
      toast.success("Thank you! Your quote request has been sent.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us on " + CONTACT.phone + ".");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-gold/25 bg-cream-card px-4 py-3 text-ink placeholder:text-stone/60 outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/20";

  return (
    <>
      <Seo
        title="Contact Us | Request a Free Quote | Eminence Windows & Doors"
        description="Contact Eminence Windows & Doors. Call 0161 383 8854 or email enquiries@eminencewindows.co.uk. Open 9am–9pm, Mon to Sat. Request your free, no-obligation quote today."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-charcoal pt-40 pb-20 text-cream grain" data-testid="contact-hero">
        <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
          <p className="kicker text-gold">Book your free consultation</p>
          <h1 className="mt-6 font-serif text-5xl leading-[1] md:text-7xl">
            <MaskReveal lines={["Let's transform", "your home."]} />
          </h1>
          <p className="mt-6 max-w-xl text-cream/80">
            No obligation, no pressure — just honest advice and a free quote for
            the perfect windows and doors for your home.
          </p>
        </div>
      </section>

      <Marquee
        items={["Free No-Obligation Quotes", "CERTASS Certified Installers", "Greater Manchester", "10 Year Insurance-Backed Guarantee", "Expert Installation"]}
      />

      {/* Contact + Form */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 md:py-28">
        <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          {/* Details */}
          <Reveal>
            <h2 className="font-serif text-3xl text-ink md:text-4xl">Get in touch</h2>
            <p className="mt-4 leading-relaxed text-stone">
              Speak to our friendly team directly, or send us your details and
              we'll be in touch at a time that suits you.
            </p>

            <div className="mt-10 space-y-6" data-testid="contact-details">
              {[
                { Icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, test: "contact-phone" },
                { Icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, test: "contact-email" },
                { Icon: Clock, label: "Opening hours", value: CONTACT.hours, test: "contact-hours" },
                { Icon: Building2, label: "Office", value: CONTACT.address, test: "contact-office" },
                { Icon: MapPin, label: "Service area", value: `Serving ${CONTACT.area}`, test: "contact-area" },
              ].map(({ Icon, label, value, href, test }) => (
                <div key={label} className="flex items-start gap-4" data-testid={test}>
                  <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gold/15">
                    <Icon className="h-5 w-5 text-gold-deep" />
                  </span>
                  <div>
                    <p className="kicker text-stone">{label}</p>
                    {href ? (
                      <a
                        href={href}
                        onClick={() => {
                          if (label === "Phone") gaEvent("phone_click", { link_location: "contact_page" });
                          if (label === "Email") gaEvent("email_click", { link_location: "contact_page" });
                        }}
                        className="mt-1 block font-serif text-2xl text-ink hover:text-gold-deep transition-colors"
                      >
                        {value}
                      </a>
                    ) : (
                      <p className="mt-1 font-serif text-2xl text-ink">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border border-gold/20 bg-cream-deep p-6">
              <p className="font-serif text-xl text-ink">Save up to £200 per window &amp; £300 per door</p>
              <p className="mt-2 text-sm text-stone">
                Ask about our recycling scheme when you enquire.
              </p>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1}>
            <div
              id="quote"
              className="scroll-mt-28 rounded-3xl border border-gold/20 bg-white/60 p-7 shadow-[0_30px_60px_-40px_rgba(28,28,28,0.5)] md:p-10"
              data-testid="quote-form-card"
            >
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  data-testid="quote-success"
                >
                  <CheckCircle2 className="h-16 w-16 text-gold" />
                  <h3 className="mt-6 font-serif text-3xl text-ink">Thank you!</h3>
                  <p className="mt-3 max-w-sm text-stone">
                    Your free quote request has been sent. Our team will be in
                    touch shortly. For anything urgent, call {CONTACT.phone}.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-8 rounded-full border border-gold/50 px-6 py-3 text-sm font-medium text-ink transition hover:bg-gold/10"
                    data-testid="quote-another-btn"
                  >
                    Submit another request
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="quote-form">
                  <h2 className="font-serif text-3xl text-ink">Request a Free Quote</h2>
                  <p className="mt-2 text-sm text-stone">Fields marked * are required.</p>

                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="kicker text-stone" htmlFor="name">Full name *</label>
                      <input id="name" className={`mt-2 ${inputCls}`} value={form.name} onChange={set("name")} placeholder="Jane Smith" data-testid="input-name" />
                    </div>
                    <div>
                      <label className="kicker text-stone" htmlFor="phone">Phone *</label>
                      <input id="phone" className={`mt-2 ${inputCls}`} value={form.phone} onChange={set("phone")} placeholder="07123 456789" data-testid="input-phone" />
                    </div>
                    <div>
                      <label className="kicker text-stone" htmlFor="email">Email *</label>
                      <input id="email" type="email" className={`mt-2 ${inputCls}`} value={form.email} onChange={set("email")} placeholder="jane@email.com" data-testid="input-email" />
                    </div>
                    <div>
                      <label className="kicker text-stone" htmlFor="postcode">Postcode *</label>
                      <input id="postcode" className={`mt-2 ${inputCls}`} value={form.postcode} onChange={set("postcode")} placeholder="M1 1AA" data-testid="input-postcode" />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="kicker text-stone">Product type *</label>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="product-type-group">
                      {PRODUCT_TYPES.map((t) => {
                        const active = form.product_type === t;
                        return (
                          <button
                            type="button"
                            key={t}
                            onClick={() => setForm((f) => ({ ...f, product_type: t }))}
                            data-testid={`product-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-medium transition ${
                              active
                                ? "border-gold bg-gold text-charcoal"
                                : "border-gold/30 bg-cream-card text-ink hover:border-gold/60"
                            }`}
                          >
                            {active && <Check className="h-4 w-4" />}
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="kicker text-stone" htmlFor="preferred">Preferred contact time</label>
                    <input id="preferred" className={`mt-2 ${inputCls}`} value={form.preferred_contact_time} onChange={set("preferred_contact_time")} placeholder="e.g. weekday evenings" data-testid="input-preferred-time" />
                  </div>

                  <div className="mt-6">
                    <label className="kicker text-stone" htmlFor="details">Project details</label>
                    <textarea id="details" rows={4} className={`mt-2 ${inputCls} resize-none`} value={form.project_details} onChange={set("project_details")} placeholder="Tell us a little about what you're looking for…" data-testid="input-details" />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-quote-btn"
                    className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-charcoal px-8 py-4 text-sm font-medium text-cream transition-all hover:bg-charcoal-soft disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                    ) : (
                      "Send My Free Quote Request"
                    )}
                  </button>
                  <p className="mt-3 text-center text-xs text-stone">
                    We'll use your details to respond to your enquiry and provide your
                    quotation. See our{" "}
                    <Link to="/privacy" className="underline hover:text-gold-deep">Privacy Notice</Link>{" "}
                    for more information.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
